import { useEffect, useState, useCallback } from "react";
import { api } from "@/sales/lib/api";
import { toast } from "sonner";
import {
  Users as UsersIcon,
  Plus,
  ShieldCheck,
  UserCircle2,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROLE_META = {
  bestuurslid: {
    label: "Bestuurslid",
    className:
      "bg-gradient-to-r from-[#A07527] to-[#D4A24C] text-[#1C1407] border border-[#E2C77C]/60",
  },
  reseller: {
    label: "Reseller",
    className: "bg-[#1C3A5A] text-[#93C5FD] border border-[#3B6FB5]/60",
  },
  gast: {
    label: "Gast",
    className: "bg-[#1E293B] text-[#94A3B8] border border-[#475569]",
  },
};

const emptyForm = { name: "", username: "", role: "reseller" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error("Kon gebruikers niet laden", {
        description: err?.response?.data?.detail || String(err),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenNew = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.username.trim()) {
      toast.error("Vul naam en gebruikersnaam in");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/users", {
        name: form.name.trim(),
        username: form.username.trim().toLowerCase(),
        role: form.role,
      });
      toast.success("Gebruiker toegevoegd", {
        description: `${form.name} (${form.username.toLowerCase()}) · standaard wachtwoord ingesteld`,
      });
      setDialogOpen(false);
      setForm(emptyForm);
      await fetchUsers();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Aanmaken mislukt", {
        description: typeof detail === "string" ? detail : String(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="w-full max-w-[1280px] mx-auto px-6 md:px-10 pb-16">
      <section className="fade-up pt-2">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#C0C0C0] border border-[rgba(192,192,192,0.3)] rounded-full px-3 py-1">
            <ShieldCheck size={11} /> Super-Admin
          </span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#94A3B8]">
            Toegangsbeheer
          </span>
        </div>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-medium text-[#F8FAFC]">
              Gebruikersbeheer
            </h2>
            <p className="text-sm text-[#94A3B8] mt-1 max-w-2xl leading-relaxed">
              Beheer resellers en gasten. Nieuwe gebruikers krijgen
              automatisch het standaard initiële wachtwoord toegewezen.
            </p>
          </div>
          <Button
            data-testid="new-user-btn"
            onClick={handleOpenNew}
            className="bg-[#16A34A] hover:bg-[#15803D] text-white font-medium rounded px-4 py-2 transition-all border-0 flex-shrink-0"
          >
            <Plus size={16} className="mr-1.5" />
            Nieuwe Gebruiker
          </Button>
        </div>
      </section>

      <section className="mt-8">
        <div className="rounded-lg border border-[rgba(192,192,192,0.15)] bg-[#0F172A] overflow-hidden">
          <div className="bg-[#1C3A5A] px-5 py-3 flex items-center gap-3">
            <UsersIcon size={15} className="text-white" />
            <h3 className="text-sm font-semibold tracking-wide text-white">
              Geregistreerde gebruikers
            </h3>
            <span className="ml-auto text-[11px] text-white/75">
              {loading ? "—" : `${users.length} accounts`}
            </span>
          </div>

          {loading ? (
            <div className="p-5 space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12 bg-[#142539]/60" />
              ))}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#64748B] border-b border-[rgba(192,192,192,0.1)]">
                  <th className="px-5 py-3 font-semibold">Naam</th>
                  <th className="px-5 py-3 font-semibold">Gebruikersnaam</th>
                  <th className="px-5 py-3 font-semibold">Rol</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, idx) => {
                  const meta = ROLE_META[u.role] || ROLE_META.gast;
                  return (
                    <tr
                      key={u.username}
                      data-testid={`user-row-${u.username}`}
                      className={`border-b border-[rgba(192,192,192,0.06)] last:border-b-0 ${
                        idx % 2 === 0 ? "bg-[#0B1521]/40" : "bg-transparent"
                      } hover:bg-[#1C3A5A]/30 transition-colors`}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <UserCircle2 size={18} className="text-[#94A3B8]" />
                          <span className="text-[#E2E8F0] font-medium">
                            {u.display_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 font-mono text-xs text-[#94A3B8]">
                        {u.username}
                      </td>
                      <td className="px-5 py-3.5">
                        <span
                          data-testid={`user-role-${u.username}`}
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-semibold rounded-full px-2.5 py-1 ${meta.className}`}
                        >
                          <ShieldCheck size={10} />
                          {meta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-5 py-8 text-center text-sm text-[#64748B] italic"
                    >
                      Nog geen gebruikers geregistreerd.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Create user dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="new-user-dialog"
          className="bg-[#142539] border-[rgba(192,192,192,0.2)] text-[#F8FAFC] max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl font-medium text-[#F8FAFC]">
              Nieuwe Gebruiker
            </DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Voeg een reseller of gast toe. Het standaard wachtwoord
              wordt automatisch ingesteld.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">
                Volledige Naam <span className="text-rose-400">*</span>
              </Label>
              <Input
                data-testid="new-user-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Sander van Dijk"
                className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">
                Gebruikersnaam <span className="text-rose-400">*</span>
              </Label>
              <Input
                data-testid="new-user-username"
                value={form.username}
                onChange={(e) =>
                  setForm({ ...form, username: e.target.value.toLowerCase() })
                }
                placeholder="sander"
                className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC] font-mono"
                required
              />
              <p className="text-[10px] text-[#64748B]">
                Alleen kleine letters, cijfers, '-' en '_' toegestaan.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">
                Rol
              </Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger
                  data-testid="new-user-role"
                  className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#142539] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]">
                  <SelectItem value="reseller" className="focus:bg-[#1C3A5A]">
                    Reseller · ziet eigen trajecten
                  </SelectItem>
                  <SelectItem value="gast" className="focus:bg-[#1C3A5A]">
                    Gast · alleen lezen
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2 sm:gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-[rgba(192,192,192,0.3)] bg-transparent text-[#E2E8F0] hover:bg-[#1C3A5A] hover:text-white"
              >
                Annuleren
              </Button>
              <Button
                type="submit"
                data-testid="new-user-submit"
                disabled={submitting}
                className="bg-[#16A34A] hover:bg-[#15803D] text-white font-medium"
              >
                {submitting ? "Bezig…" : "Toevoegen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
